import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
} from '@mui/material';
import { ErrorOutline as ErrorIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import Papa from 'papaparse';
import { supabase } from '../lib/supabase';

type BulkSimAssignModalProps = {
  open: boolean;
  onClose: () => void;
};

// Enum for different stages of the upload process
enum UploadStage {
  Idle,       // Waiting for file selection
  Validating, // File selected, parsing and validating
  Ready,      // Validation complete, ready to submit
  Submitting, // Data sent to backend
  Complete,   // Backend processing finished
}

// Define expected CSV Row structure
type CsvRow = {
    iccid: string;
    msisdn?: string; // Optional
    organization_name: string;
    cluster_name: string;
};

// Type for validated data to be submitted
type ValidatedSimData = {
    iccid: string;
    msisdn: string | null;
    organization_id: string;
    cluster_id: string;
}

// Helper function to wrap Papa.parse in a Promise
function parseCsvAsync(file: File): Promise<Papa.ParseResult<CsvRow>> {
    return new Promise((resolve, reject) => {
        Papa.parse<CsvRow>(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results: Papa.ParseResult<CsvRow>) => resolve(results),
            error: (error: Error) => reject(error),
        });
    });
}

export function BulkSimAssignModal({ open, onClose }: BulkSimAssignModalProps) {
  const [stage, setStage] = useState<UploadStage>(UploadStage.Idle);
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validRowCount, setValidRowCount] = useState<number>(0);
  const [totalRowCount, setTotalRowCount] = useState<number>(0);
  const [validatedSims, setValidatedSims] = useState<ValidatedSimData[]>([]);

  // Reset state when modal closes or opens
  useEffect(() => {
    if (open) {
      setStage(UploadStage.Idle);
      setFileName('');
      setError(null);
      setValidationErrors([]);
      setValidRowCount(0);
      setTotalRowCount(0);
      setValidatedSims([]);
    }
  }, [open]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
        if (selectedFile.type !== 'text/csv') {
            setError('Invalid file type. Please upload a CSV file.');
            setFileName('');
            return;
        }
      setFileName(selectedFile.name);
      setError(null);
      setValidationErrors([]);
      setValidRowCount(0);
      setTotalRowCount(0);
      setStage(UploadStage.Validating);
      validateCsv(selectedFile);
    } else {
        setFileName('');
        setStage(UploadStage.Idle);
    }
  };

  const validateCsv = useCallback(async (csvFile: File) => {
    console.log('Starting validation for:', csvFile.name);
    setStage(UploadStage.Validating);
    setValidationErrors([]);
    setValidatedSims([]);
    setError(null);

    let currentErrors: string[] = [];
    let currentValidData: ValidatedSimData[] = [];
    let rowCount = 0;

    try {
        // 1. Fetch validation lookup data
        console.log('Fetching validation data...');
        // Define types for fetched data
        type OrgData = { id: string; name: string };
        type ClusterData = { id: string; name: string; organization_id: string };
        type IccidData = { iccid: string };

        const [orgRes, clusterRes, iccidRes] = await Promise.all([
            supabase.from('organizations').select<'id, name', OrgData>('id, name'),
            supabase.from('clusters').select<'id, name, organization_id', ClusterData>('id, name, organization_id'),
            supabase.from('sim_cards').select<'iccid', IccidData>('iccid').not('iccid', 'is', null)
        ]);

        if (orgRes.error) throw new Error(`Failed to fetch organizations: ${orgRes.error.message}`);
        if (clusterRes.error) throw new Error(`Failed to fetch clusters: ${clusterRes.error.message}`);
        if (iccidRes.error) throw new Error(`Failed to fetch existing ICCIDs: ${iccidRes.error.message}`);

        // Explicitly type the Maps
        const orgMap = new Map<string, string>(orgRes.data?.map((o: OrgData) => [o.name.toLowerCase(), o.id]) || []);
        const clusterMap = new Map<string, string>(clusterRes.data?.map((c: ClusterData) => [`${c.organization_id}|${c.name.toLowerCase()}`, c.id]) || []);
        const existingIccids = new Set<string>(iccidRes.data?.map((s: IccidData) => s.iccid) || []);
        console.log(`Fetched ${orgMap.size} orgs, ${clusterMap.size} clusters, ${existingIccids.size} existing ICCIDs.`);

        // 2. Parse CSV using the async helper
        console.log('Parsing CSV...');
        const results = await parseCsvAsync(csvFile);

        if (results.errors.length > 0) {
            console.error('Papaparse errors:', results.errors);
            throw new Error(`CSV parsing failed: ${results.errors[0].message}`);
        }
        
        console.log(`Parsed ${results.data.length} rows.`);
        rowCount = results.data.length;
        setTotalRowCount(rowCount);

        // 3. Validate each row (results.data is now CsvRow[])
        for (let i = 0; i < results.data.length; i++) {
            const row = results.data[i]; // row is now type CsvRow
            const rowIndex = i + 2; // Account for header row and 0-based index
            let rowValid = true;
            let rowErrors: string[] = [];

            const iccid = row.iccid?.trim();
            const msisdn = row.msisdn?.trim() || null;
            const orgName = row.organization_name?.trim();
            const clusterName = row.cluster_name?.trim();

            // Check required fields
            if (!iccid) { rowErrors.push(`Row ${rowIndex}: Missing required column 'iccid'.`); rowValid = false; }
            if (!orgName) { rowErrors.push(`Row ${rowIndex}: Missing required column 'organization_name'.`); rowValid = false; }
            if (!clusterName) { rowErrors.push(`Row ${rowIndex}: Missing required column 'cluster_name'.`); rowValid = false; }

            // Validate ICCID format (example: numeric and length)
            if (iccid && (!/^[\d]+$/.test(iccid) || iccid.length < 18 || iccid.length > 22)) { // Adjust length as needed
                 rowErrors.push(`Row ${rowIndex}: Invalid ICCID format '${iccid}'. Should be numeric, 18-22 digits.`); rowValid = false; 
            }
            // Check for existing ICCID
            if (iccid && existingIccids.has(iccid)) {
                rowErrors.push(`Row ${rowIndex}: ICCID '${iccid}' already exists in the system.`); rowValid = false;
            }

            // Validate Organization
            const orgId = orgName ? orgMap.get(orgName.toLowerCase()) : undefined;
            if (orgName && !orgId) {
                rowErrors.push(`Row ${rowIndex}: Organization '${orgName}' not found.`); rowValid = false;
            }

            // Validate Cluster (only if org is valid)
            let clusterId: string | undefined = undefined;
            if (orgId && clusterName) {
                 // Type for Map.get is known now
                 clusterId = clusterMap.get(`${orgId}|${clusterName.toLowerCase()}`);
                 if (!clusterId) {
                     rowErrors.push(`Row ${rowIndex}: Cluster '${clusterName}' not found within Organization '${orgName}'.`); rowValid = false;
                 }
            }

            // Collect errors or valid data
            // Use non-null assertions where logic guarantees they exist
            if (rowValid && orgId && clusterId && iccid) {
                 currentValidData.push({ 
                     iccid, 
                     msisdn, 
                     organization_id: orgId!, // Assert non-null based on check
                     cluster_id: clusterId! // Assert non-null based on check
                 });
            } else {
                 currentErrors = currentErrors.concat(rowErrors);
            }
        }

        // 4. Update state with results
        setValidatedSims(currentValidData);
        setValidRowCount(currentValidData.length);
        setValidationErrors(currentErrors);

        if (currentErrors.length > 0 || currentValidData.length === 0) {
            setError(`Validation completed with errors. Please fix the issues in your CSV or upload a new file.`);
            setStage(UploadStage.Idle); // Revert to Idle to allow re-upload
        } else {
            setError(null);
            setStage(UploadStage.Ready);
        }

    } catch (err: any) {
        console.error('Validation error:', err);
        setError(`Validation failed: ${err.message}`);
        setStage(UploadStage.Idle); // Revert to Idle on critical error
    } finally {
        console.log('Validation complete.');
        // If Validating stage had a spinner, remove it here, but LinearProgress handles itself
    }
  }, []);

  const handleSubmit = async () => {
    if (stage !== UploadStage.Ready || validatedSims.length === 0) {
        setError('No valid data available to submit.');
        return;
    }
    setStage(UploadStage.Submitting);
    setError(null);
    
    console.log(`Submitting ${validatedSims.length} valid SIMs...`);

    try {
        // Call Supabase Edge Function
        const { error: functionError } = await supabase.functions.invoke('bulk-assign-sims', {
            body: { sims: validatedSims },
        });

        if (functionError) {
            throw new Error(`Edge function execution failed: ${functionError.message}`);
        }
        
        console.log('Submission successful via Edge Function.')
        setStage(UploadStage.Complete);

    } catch (err: any) {
        console.error('Submission error:', err);
        setError(`Submission failed: ${err.message}. Check Edge Function logs for details.`);
        setStage(UploadStage.Ready); // Revert to ready state on failure
    } 
  };

  const renderContent = () => {
    switch (stage) {
      case UploadStage.Idle:
      case UploadStage.Validating:
        return (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
              disabled={stage === UploadStage.Validating}
            >
              Select CSV File
              <input type="file" accept=".csv" hidden onChange={handleFileChange} />
            </Button>
            {fileName && <Typography sx={{ mt: 1 }}>Selected: {fileName}</Typography>}
            {stage === UploadStage.Validating && (
                <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress />
                    <Typography variant="body2" sx={{ mt: 1 }}>Validating...</Typography>
                </Box>
            )}
             {/* Show validation errors if any */}
            {validationErrors.length > 0 && (
                <Box sx={{ mt: 2, textAlign: 'left' }}>
                    <Typography color="error">Validation Errors:</Typography>
                    <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                        {validationErrors.map((err, index) => (
                            <ListItem key={index} disableGutters>
                                <ListItemIcon sx={{minWidth: '30px'}}><ErrorIcon color="error" fontSize="small" /></ListItemIcon>
                                <ListItemText primary={err} primaryTypographyProps={{ variant: 'body2' }} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
          </Box>
        );
      case UploadStage.Ready:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success">
              Validation successful. Found {validRowCount} valid rows out of {totalRowCount}. Ready to submit.
            </Alert>
          </Box>
        );
      case UploadStage.Submitting:
        return (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1 }}>Submitting {validRowCount} SIMs...</Typography>
          </Box>
        );
      case UploadStage.Complete:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success">
              Bulk assignment complete! {validRowCount} SIMs were processed.
            </Alert>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={stage === UploadStage.Submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Assign SIM Cards</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" gutterBottom>
          Upload a CSV file with the following columns: 
          <code>iccid</code>, <code>msisdn</code> (optional), <code>organization_name</code>, <code>cluster_name</code>.
        </Typography>
        <Typography variant="body2">
            Download template: <Link href="#" onClick={(e) => e.preventDefault()}>template.csv</Link> {/* TODO: Link to actual template */}
        </Typography>
        
        {renderContent()}
        
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={stage === UploadStage.Submitting}>Cancel</Button>
        {stage === UploadStage.Ready && (
            <Button onClick={handleSubmit} variant="contained">
                Submit Valid SIMs
            </Button>
        )}
         {stage === UploadStage.Complete && (
            <Button onClick={onClose} variant="contained">
                Done
            </Button>
        )}
      </DialogActions>
    </Dialog>
  );
} 